import * as React from 'react';
import {
    DateField,
    FunctionField,
    ReferenceField,
    Show,
    SimpleShowLayout,
    TextField,
    useNotify,
    useRedirect,
    useRefresh,
    useShowContext,
} from 'react-admin';
import { WrappedWithBackButton } from '../common/wrapped-with-back-button';
import { getRequestStatusName } from '../enum-translates';
import { Box, Button } from '@mui/material';
import { config } from '../config';
import { getJwtHeader } from '../auth/authProvider';
import {
    getStatusColor,
    StatusChangeButton,
    StatusHistory,
} from '../common/status-history';
import { Chip } from '@material-ui/core';
import { CustomLabel } from '../common/custom-label';
import { UniqueRequestMessageList } from './message/unique-request-message-list';
import { useState } from 'react';
import { UniqueRequestAdminDialog } from './unique-request-admin-dialog';
import { WorkStatusType } from '../models/work-status';
import { useTheme } from '@mui/material/styles';
import { UniqueRequest } from '../models/unique-request';
import { UniqueRequestTypeChip } from './unique-request-type';

export const UniqueRequestShowLayout = () => {
    const theme = useTheme();
    const { error, isLoading, record } = useShowContext();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();

    const [dialogOpen, setDialogOpen] = useState(false);

    if (isLoading || record === null) {
        return <div>Betöltés...</div>;
    }
    if (error) {
        return <div>Hiba történt!</div>;
    }

    const changeStatus = async (status: string) => {
        fetch(`${config.apiUrl}/unique-request/${record.id}/status`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: getJwtHeader(),
            },
            body: JSON.stringify({
                status,
            }),
        }).then(async (res) => {
            if (!res.ok) {
                await res.text().then((text) => {
                    notify(text, { type: 'error' });
                });
            } else {
                notify('Sikeres státusz változtatás!', { type: 'success' });
                if (status === 'Free' || status === 'UnderDuty') {
                    redirect('list', 'unique-request');
                } else {
                    refresh();
                }
            }
        });
    };

    return (
        <SimpleShowLayout>
            <TextField source="caseNumber" label={'Ikatatószám'} />
            <FunctionField
                label="Típus"
                render={(record: UniqueRequest) => (
                    <UniqueRequestTypeChip type={record.type} />
                )}
            />
            <DateField
                source="createdAt"
                locales={['hu-HU']}
                label={'Létrehozva'}
                showTime
            />
            <ReferenceField
                source="user.id"
                reference="web-user"
                link="show"
                label={'Felhasználó'}
                emptyText="Törölt felhasználó"
            >
                <TextField source="name" />
            </ReferenceField>
            <TextField source="subject" label={'Tárgy'} />
            <TextField source={'insideNote'} label={'Admin megjegyzés'} />
            <>
                <Button
                    variant={'contained'}
                    onClick={() => setDialogOpen(true)}
                >
                    Megjegyzés
                </Button>
            </>
            <UniqueRequestAdminDialog
                record={record}
                open={dialogOpen}
                setOpen={setDialogOpen}
            />
            {record?.status?.status && (
                <>
                    <CustomLabel>Státusz</CustomLabel>
                    <Box style={{ fontSize: 1, margin: '10px 0' }}>
                        <Chip
                            style={{
                                backgroundColor: getStatusColor(
                                    theme,
                                    record.status.status
                                ),
                                color: '#FFF',
                            }}
                            label={getRequestStatusName(record.status.status)}
                        />
                    </Box>
                    <CustomLabel>Státusz változtatás</CustomLabel>
                    <Box
                        style={{
                            display: 'flex',
                            gap: 7,
                            marginTop: 3,
                        }}
                    >
                        {record.status.status !== WorkStatusType.Free && (
                            <StatusChangeButton
                                status={'Free'}
                                changeStatus={changeStatus}
                            />
                        )}
                        {record.status.status !== WorkStatusType.InProgress && (
                            <StatusChangeButton
                                status={'InProgress'}
                                changeStatus={changeStatus}
                            />
                        )}
                        {record.status.status !== WorkStatusType.UnderDuty && (
                            <StatusChangeButton
                                status={'UnderDuty'}
                                changeStatus={changeStatus}
                            />
                        )}
                        {record.status.status !== WorkStatusType.Closed && (
                            <StatusChangeButton
                                status={'Closed'}
                                changeStatus={changeStatus}
                            />
                        )}
                    </Box>
                </>
            )}
            <UniqueRequestMessageList record={record} />
            {record.statusHistory && record.statusHistory.length && (
                <StatusHistory history={record.statusHistory} />
            )}
        </SimpleShowLayout>
    );
};

export const UniqueRequestShow = () => {
    return (
        <WrappedWithBackButton>
            <Show title={'Bejelentés'}>
                <UniqueRequestShowLayout />
            </Show>
        </WrappedWithBackButton>
    );
};
